import {
  Building2,
  CalendarDays,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  ScanLine,
  Users,
  UserCog
} from "lucide-react";
import type { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { api } from "../api/client.js";
import { useAuth, useScope } from "../store/auth.js";
import { cn } from "../lib/utils.js";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** When true, only portal admins see this item. */
  portalOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/customers", label: "Customers", icon: Building2, portalOnly: true },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/operators", label: "Operators", icon: ScanLine },
  { to: "/admin-users", label: "Admin users", icon: UserCog },
  { to: "/audit", label: "Audit log", icon: ClipboardList },
  { to: "/chat", label: "AI Chat", icon: MessageSquare }
];

/**
 * The authenticated shell — fixed sidebar nav plus the routed page content.
 * Portal-only nav items are hidden for customer admins (defence in depth; the
 * routes themselves also render a 403 view).
 */
export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const { isPortalAdmin } = useScope();
  const navigate = useNavigate();
  const location = useLocation();
  // Routes that take the full main pane (no max-width cap, no padding).
  // /chat is full-bleed so the conversation has the entire viewport to work
  // with — pages with tables/cards keep the centred 6xl shell.
  const flush = location.pathname.startsWith("/chat");

  const handleLogout = async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Logout is best-effort — clear the session regardless.
    }
    logout();
    navigate("/login");
  };

  const items = NAV_ITEMS.filter((item) => !item.portalOnly || isPortalAdmin);

  return (
    // h-screen pins the outer to the viewport so the sidebar never scrolls
    // out of view. Only the <main> pane (and, when the nav list overflows
    // on a very short screen, the <nav> inside the aside) actually scroll.
    // The user card + logout footer in the aside stays pinned at the bottom.
    <div className="flex h-screen bg-secondary/40">
      <aside className="flex h-screen w-60 shrink-0 flex-col border-r border-border bg-background">
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-border px-6">
          <span className="text-base font-semibold tracking-tight">Attendik</span>
          <span className="text-sm text-muted-foreground">Admin</span>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="shrink-0 border-t border-border p-3">
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{user?.name ?? "—"}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {user?.role === "portal_admin" ? "Portal admin" : "Customer admin"}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        {flush ? (
          <div className="h-full">{children}</div>
        ) : (
          <div className="mx-auto max-w-6xl p-8">{children}</div>
        )}
      </main>
    </div>
  );
}

/** Generic page-level header — title plus optional actions. */
export function PageHeader({
  title,
  description,
  actions
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export { Users };
